import { useMemo } from 'react';

import { faTimes } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';

import { SelectableOption } from '@/common-components/elements/SelectableOption/SelectableOption';

import type { FeedbackOption } from './types';

interface InlineFeedbackBoxProps {
  rating: 'positive' | 'negative';
  positiveOptions: FeedbackOption[];
  negativeOptions: FeedbackOption[];
  selectedFeedbackOptions: string[];
  className?: string;
  onToggleOption: (value: string) => void;
  onClose: () => void;
  onMoreClick: () => void;
  onSubmit: () => void;
}

export const InlineFeedbackBox = ({
  rating,
  positiveOptions,
  negativeOptions,
  selectedFeedbackOptions,
  className,
  onToggleOption,
  onClose,
  onMoreClick,
  onSubmit,
}: InlineFeedbackBoxProps) => {
  const currentOptions = useMemo(() => {
    return rating === 'positive' ? positiveOptions : negativeOptions;
  }, [rating, positiveOptions, negativeOptions]);

  const feedbackOptions = useMemo(
    () => (
      <div className="vai-flex vai-flex-wrap vai-gap-2 vai-w-full">
        {currentOptions.map((option) => (
          <SelectableOption
            key={option.value}
            value={option.value}
            isSelected={selectedFeedbackOptions.includes(option.value)}
            onToggle={onToggleOption}
          >
            {option.label}
          </SelectableOption>
        ))}
        <SelectableOption
          value="more"
          isSelected={false}
          onToggle={onMoreClick}
        >
          <Trans>More...</Trans>
        </SelectableOption>
      </div>
    ),
    [currentOptions, selectedFeedbackOptions, onToggleOption, onMoreClick],
  );

  return (
    <div
      className={`vai-p-4 vai-mt-2 vai-bg-white vai-border vai-rounded-lg vai-w-full vai-max-w-full vai-overflow-hidden ${className}`}
    >
      <div className="vai-flex vai-items-start vai-justify-between vai-mb-3">
        <h3 className="vai-text-sm vai-text-zinc-600">
          <Trans>Tell us more:</Trans>
        </h3>
        <button
          onClick={onClose}
          className="vai-text-gray-400 hover:vai-text-gray-600 vai-transition-colors vai-flex-shrink-0 vai-self-start vai-align-top vai-flex vai-items-start"
          aria-label="Close feedback"
        >
          <FontAwesomeIcon icon={faTimes} className="vai-text-sm" />
        </button>
      </div>
      <div className="vai-overflow-x-auto vai-mb-4">{feedbackOptions}</div>
      <div className="vai-flex vai-justify-end vai-pt-4 vai-border-t vai-border-gray-200 -vai-mx-4 vai-px-4">
        <Button
          onClick={onSubmit}
          disabled={selectedFeedbackOptions.length === 0}
          data-testid="inline-feedback-submit-button"
        >
          <Trans>Submit feedback</Trans>
        </Button>
      </div>
    </div>
  );
};
